/**
 * CC 结果报告查询参数。
 *
 * 接口文档只接受 task_id，因此调用方不应附加专利号等冗余字段。
 */
export interface CcReportRequest {
    task_id: string;
}

/** CC 报告摘要统计。 */
export interface CcReportSummary {
    cc_feature_count: number;
    full_mapping_count: number;
    implicit_mapping_count: number;
    no_mapping_count: number;
}

/** Passport 区域中的一行展示数据。 */
export interface CcReportPassportItem {
    label: string;
    content: string;
}

export interface CcReportPassport {
    items: CcReportPassportItem[];
}

/** 权利要求原文及后端拆分出的特征。 */
export interface CcReportClaimAnalysis {
    original_claim: string;
    claim_features: string[];
}

/**
 * 同族申请表格中的单条申请。
 *
 * 这里保留接口的 snake_case 字段，避免在页面层制造第二套别名。
 */
export interface CcReportFamilyApplicationItem {
    publication_number: string;
    publication_date: string;
    legal_status: string;
    legal_color?: string | null;
    application_number: string;
    application_date: string;
    estimated_expiry_date: string;
}

export interface CcReportFamilyApplicationAnalysis {
    text: string;
    items: CcReportFamilyApplicationItem[];
}

/**
 * 标准证据中的单个高亮区间。
 *
 * end 按 String.slice 的结束下标处理，即不包含结束位置字符。
 */
export interface CcReportEvidenceHighlightRange {
    start: number;
    end: number;
}

/**
 * 后端为 Word 与网页统一生成的标准证据高亮片段。
 *
 * priority 数值越大，重叠时展示层级越高。例如实体映射会覆盖核心句底色。
 */
export interface CcReportEvidenceHighlightSpan extends CcReportEvidenceHighlightRange {
    color: string;
    type: 'core_sentence' | 'entity_mapping' | string;
    priority: number;
}

/**
 * 标准证据中的上下文与高亮下标。
 *
 * highlight_end 按 String.slice 的结束下标处理，即不包含结束位置字符。
 * highlight_spans、highlight_ranges 来自实际报告接口，用于还原 Word 中的
 * 多色实体高亮和不连续核心句高亮。
 */
export interface CcReportEvidenceContext {
    context_text: string;
    highlight_start: number;
    highlight_end: number;
    highlight_ranges?: CcReportEvidenceHighlightRange[];
    highlight_spans?: CcReportEvidenceHighlightSpan[];
}

/** 一段标准条款证据。同一权利要求特征可能对应多段 mappings。 */
export interface CcReportStandardEvidence {
    ts_code: string;
    version: string;
    ts_section: string[];
    ts_content?: string | string[];
    suggested_ts_content?: string | string[];
    entity_mapping?: Record<string, string>;
    evidence_contexts?: CcReportEvidenceContext[];
}

export interface CcReportJudgeStance {
    side?: string;
    reason?: string;
    [key: string]: unknown;
}

/** 权利要求特征与标准证据之间的映射结论。 */
export interface CcReportFeatureEvidenceMapping {
    claim_feature: string;
    /** claim_feature 的完整高亮配置，直接用于还原 Word 中的权利要求特征颜色。 */
    claim_highlights: CcReportEvidenceHighlightSpan[];
    mapping_label: string;
    judge_stance?: CcReportJudgeStance | string | null;
    stance?: CcReportJudgeStance | string | null;
    stance_strength?: string | null;
    review_analysis: string;
    reason: string;
    mappings: CcReportStandardEvidence[];
}

export interface CcReportTextSection {
    text: string;
}

/** 多 Claim 报告中按 Claim 分组的章节数据。 */
export interface CcReportClaimSection {
    task_id: string;
    claim_number: number;
    child_task_id: string;
    claim_status: string;
    claim_error?: string | null;
    overall_judgment?: string;
    judgment_tag?: string;
    summary?: CcReportSummary;
    claim_analysis?: CcReportClaimAnalysis;
    feature_evidence_mappings?: CcReportFeatureEvidenceMapping[];
    feature_evidence_highlight_style?: Record<string, unknown>;
    overall_conclusion?: CcReportTextSection;
}

/**
 * GET /telecom/aicc/cc_compare/report 的 data 字段。
 *
 * 所有属性均与接口文档一一对应，页面直接消费这些字段，不设置跨字段兜底。
 */
export interface CcReportData {
    task_id: string;
    patent_id: string;
    patent_number: string;
    title: string;
    overall_judgment: string;
    judgment_tag: string;
    priority_date: string;
    analysis_object: string;
    compare_standards: string;
    report_summary_text: string;
    summary: CcReportSummary;
    passport: CcReportPassport;
    claim_analysis?: CcReportClaimAnalysis;
    family_application_analysis: CcReportFamilyApplicationAnalysis;
    feature_evidence_mappings: CcReportFeatureEvidenceMapping[];
    risk_boundary_statement: CcReportTextSection;
    overall_conclusion: CcReportTextSection;
    aggregate_conclusion?: string;
    claim_count?: number;
    success_claim_count?: number;
    failed_claim_count?: number;
    partial_failure?: boolean;
    claim_reports?: CcReportClaimSection[];
}

/** CC 报告接口的完整响应包。 */
export interface CcReportResponse {
    status: boolean;
    data?: CcReportData;
    error_code: number;
}

export const CcReportApi = Symbol('CcReportApi');

export interface CcReportApi {
    /** 按任务 ID 获取结构化 CC 结果报告。 */
    getReport(request: CcReportRequest): Promise<CcReportResponse>;
}
